import { pluralFr, type Count } from '../lib/format'

import type { Translations } from './types'

/**
 * French. Typed against the Russian source, so every key and every function
 * signature must match. Amounts and dates arrive already formatted; the
 * dictionary only supplies words around them. Sentence case throughout, as
 * French UI convention has it; « Push », « USDT » and ticker symbols stay as is.
 */
export const fr: Translations = {
  common: {
    all: 'Max',
    back: 'Retour',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    copy: 'Copier',
    copied: 'Copié',
    open: 'Ouvrir',
    retry: 'Réessayer',
    save: 'Enregistrer',
    close: 'Fermer',
    loading: 'Chargement',
    available: 'Disponible',
    total: 'Total',
    amount: 'Montant',
    network: 'Réseau',
    asset: 'Devise',
    status: 'Statut',
    date: 'Date',
    nothingYet: 'Rien pour le moment',
    somethingWentWrong: 'Une erreur est survenue',
    reload: 'Recharger',
    crashHint: 'Cet écran n’a pas pu s’afficher. Rechargez l’application : vos fonds et vos demandes ne sont pas concernés.',
  },

  nav: {
    label: 'Navigation principale',
    home: 'Accueil',
    markets: 'Marchés',
    signals: 'Signaux',
    trade: 'Trading',
    wallet: 'Actifs',
  },

  auth: {
    signIn: 'Connexion',
    signInSubtitle: 'Connectez-vous pour retrouver vos signaux et votre solde',
    signInAction: 'Se connecter',
    signUp: 'Inscription',
    signUpSubtitle: 'Une adresse e-mail et un mot de passe suffisent pour commencer',
    signUpSubtitleInvite: 'Une adresse e-mail, un mot de passe et un code d’invitation suffisent pour commencer',
    email: 'Adresse e-mail',
    emailPlaceholder: 'nom@exemple.com',
    password: 'Mot de passe',
    passwordHint: 'Au moins 8 caractères, avec des lettres et des chiffres',
    passwordConfirm: 'Confirmez le mot de passe',
    referralCode: 'Code d’invitation',
    referralCodePlaceholder: 'Par exemple, 8K3M7QPL',
    referralCodeHint: 'Facultatif : si un membre de 19CRT vous a invité',
    referralCodeRequiredHint: 'Inscription sur invitation uniquement : utilisez le lien ou le code de la personne qui vous a invité',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} ${pluralFr(signals, 'signal bonus', 'signaux bonus')}` : null,
        promo === null ? null : `un bonus promotionnel de ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Si un membre de 19CRT vous a donné ce code, l’invitation compte après un premier dépôt d’au moins ${deposit} ${asset}`
        : `Si un membre de 19CRT vous a donné ce code, après un premier dépôt d’au moins ${deposit} ${asset} : ${perks.join(' et ')}`
    },
    forgot: 'Mot de passe oublié ?',
    createAccount: 'Créer un compte',
    haveAccount: 'Vous avez déjà un compte ?',
    noAccount: 'Pas encore de compte ?',
    backToSignIn: 'Retour à la connexion',
    verifyTitle: 'Confirmez votre e-mail',
    verifySent: (email: string) => `Un e-mail a été envoyé à ${email}`,
    verifySentAnywhere: 'Un e-mail avec le lien a été envoyé à votre adresse',
    verifyHint:
      'Ouvrez le lien contenu dans l’e-mail pour confirmer l’adresse : les e-mails de récupération du mot de passe y sont envoyés. Si vous ne le voyez pas, vérifiez le dossier « Spam ».',
    verifyEmailPrompt: 'Indiquez l’adresse e-mail liée à votre compte',
    verifyResend: 'Renvoyer l’e-mail',
    verifyResendIn: (seconds: Count) => `Nouvel envoi possible dans ${seconds.text} s`,
    verifyResent: 'Un nouvel e-mail vient de partir',
    verifyResentMaybe: 'Si l’adresse est enregistrée et pas encore confirmée, un nouvel e-mail est en route',
    verifyChecking: 'Vérification du lien',
    verifyDone: 'Adresse e-mail confirmée',
    verifyDoneHint: 'Ouverture de l’application',
    verifyDoneSignInHint: 'Ouverture de la connexion',
    resetTitle: 'Réinitialiser le mot de passe',
    resetSubtitle: 'Indiquez votre e-mail : nous vous enverrons un lien pour changer de mot de passe',
    resetSend: 'Envoyer le lien',
    resetSent: 'Si ce compte existe, l’e-mail est déjà parti',
    resetSentHint:
      'Nous n’indiquons pas si une adresse est enregistrée : ce formulaire permettrait sinon de tester les adresses d’autres personnes. Regardez dans « Boîte de réception » et « Spam ».',
    newPassword: 'Nouveau mot de passe',
    newPasswordSubtitle: 'Choisissez un nouveau mot de passe. L’ancien cessera de fonctionner immédiatement.',
    newPasswordSubmit: 'Enregistrer le mot de passe',
    requestNewLink: 'Demander un nouveau lien',
    newPasswordDone: 'Mot de passe mis à jour',
    newPasswordDoneHint: 'Ouverture de la connexion',
    signOut: 'Se déconnecter',
    totpCode: 'Code de confirmation',
    totpHint: 'Les six chiffres de votre application d’authentification',
    recoveryCode: 'Code de récupération',
    recoveryCodeHint: 'Huit caractères parmi les codes enregistrés à l’activation de la 2FA',
    useRecoveryCode: 'Utiliser un code de récupération',
    useAppCode: 'Utiliser l’application d’authentification',
    resetLinkInvalid: 'Ce lien ne fonctionne pas',
    errors: {
      invalidCredentials: 'E-mail ou mot de passe incorrect',
      emailTaken: 'Cette adresse e-mail est déjà enregistrée',
      weakPassword: 'Le mot de passe est trop court',
      passwordRule: 'Le mot de passe doit contenir des lettres et des chiffres',
      passwordsDiffer: 'Les mots de passe ne correspondent pas',
      invalidEmail: 'Saisissez une adresse au format nom@exemple.com',
      invalidReferralCode: 'Vérifiez le code d’invitation',
      referralRequired: 'Saisissez le code d’invitation',
      emailRequired: 'Indiquez votre e-mail',
      passwordRequired: 'Saisissez le mot de passe',
      accountBlocked: 'L’accès au compte est restreint. Contactez l’assistance',
      rateLimited: 'Trop de tentatives. Réessayez plus tard',
      totpRequired: 'Saisissez le code de votre application d’authentification',
      totpInvalid: 'Le code n’a pas été accepté. Vérifiez l’heure du téléphone et saisissez un nouveau code',
      totpFormat: 'Le code de l’application compte six chiffres, un code de récupération huit caractères',
      expiredToken: 'Le lien a expiré. Demandez-en un nouveau',
      verifyLinkUsed: 'Ce lien a déjà été utilisé. Connectez-vous, ou ouvrez le dernier e-mail si vous en avez demandé un nouveau',
      missingToken: 'Le lien est incomplet. Ouvrez-le entièrement depuis l’e-mail',
      validation: 'Vérifiez les champs saisis',
      network: 'Pas de connexion au serveur. Vérifiez votre réseau',
    },
  },

  home: {
    promoLabel: 'Promotions',
    banners: [
      {
        title: 'Signaux sur l’or',
        subtitle: 'Un code de notre chaîne Telegram, un trade en une étape',
        cta: 'Saisir le code',
      },
      {
        title: 'Marchés en temps réel',
        subtitle: 'Graphiques et carnet d’ordres en direct pour toutes les paires',
        cta: 'Ouvrir les marchés',
      },
      {
        title: 'Déposez en quelques minutes',
        subtitle: 'USDT sur les réseaux TRC20 et ERC20',
        cta: 'Alimenter le compte',
      },
    ],
    pauseAnnouncements: 'Mettre les annonces en pause',
    resumeAnnouncements: 'Reprendre les annonces',
    announcementsLabel: 'Annonces',
    announcements: [
      'Avant un dépôt, vérifiez la devise et le réseau du transfert',
      'Gérez la protection de votre compte dans la section « Sécurité »',
      'L’historique des opérations se trouve dans la section « Actifs »',
    ],
    totalBalance: 'Solde total',
    quickTrade: 'Trading rapide',
    quickTradeHint: 'Spot et futures dans un seul terminal',
    quickActions: 'Actions rapides',
    signalLive: 'Signal disponible',
    signalNext: 'Prochain signal',
    signalClosesIn: 'se ferme dans',
    signalOpensAt: (time: string) => `s’ouvre à ${time}`,
    signalsTitle: 'Signaux sur l’or',
    allSignals: 'Tous les signaux',
    marketMoves: 'Mouvements du marché',
    pair: 'Paire',
    lastPrice: 'Prix',
    change24h: 'Variation 24 h',
    /** After the figure in the home balance's change pill: « +1.30% sur 24 h ». */
    change24hShort: 'sur 24 h',
    volume24h: 'Volume 24 h',
    allMarkets: 'Tous les marchés',
    pricesUnavailable: 'Cours indisponibles : impossible d’évaluer tout le portefeuille',
    ratesUnavailable: 'Cours temporairement indisponibles',
    marketsUnavailable: 'La liste des marchés est temporairement indisponible',
    signalsNone: 'Aucun signal pour le moment — les nouveaux codes paraissent sur notre chaîne Telegram',
  },

  markets: {
    title: 'Marchés',
    search: 'Rechercher une paire',
    spot: 'Spot',
    futures: 'Futures',
    gainers: 'Hausses',
    losers: 'Baisses',
    volume: 'Volume 24 h',
    volumeShort: 'volume',
    typeLabel: 'Type de marché',
    sortLabel: 'Tri',
    clearSearch: 'Effacer la recherche',
    favourites: 'Favoris',
    addFavourite: 'Ajouter aux favoris',
    removeFavourite: 'Retirer des favoris',
    emptyFavourites: 'Aucun favori pour l’instant : marquez des paires d’une étoile',
    empty: 'Aucun résultat',
    emptyHint: 'Vérifiez le symbole de la paire, par exemple BTC ou SOL.',
    emptySpot: 'Aucune paire spot n’est encore ouverte',
    emptyFutures: 'Aucune paire futures n’est encore ouverte',
    loadFailed: 'Impossible de charger la liste des marchés',
    pricesUnavailable: 'Cotations temporairement indisponibles. Les prix se mettront à jour automatiquement.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Or' } as Record<string, string>,
  },

  wallet: {
    title: 'Actifs',
    totalValue: 'Valeur totale du compte',
    accounts: 'Mes comptes',
    latestWithdrawal: 'Dernier retrait',
    coin: 'Crypto',
    searchCoin: 'Rechercher une crypto',
    quickActions: 'Actions rapides',
    coins: 'Cryptomonnaies',
    hideBalance: 'Masquer le solde',
    showBalance: 'Afficher le solde',
    loadError: 'Impossible de charger le solde',
    pricesUnavailable: 'Cours indisponibles : impossible d’évaluer tout le portefeuille',
    coinsEmpty: 'Aucune cryptomonnaie pour l’instant. Alimentez votre compte : elles apparaîtront ici.',
    accountSpot: 'Compte spot',
    accountSpotHint: 'Disponible pour le trading',
    accountFutures: 'Compte futures',
    accountFuturesHint: 'Marge des positions',
    accountCopy: 'Copy trading (archive)',
    accountCopyHint: 'Transférez-le vers le spot',
    actions: {
      deposit: 'Déposer',
      withdraw: 'Retirer',
      transfer: 'Transférer',
      history: 'Historique',
    },
    deposit: {
      title: 'Dépôt',
      history: 'Historique des dépôts',
      chain: 'Réseau',
      saveQr: 'Enregistrer le QR code',
      address: 'Adresse de dépôt',
      copyAddress: 'Copier l’adresse',
      notice: 'Avis important',
      noticeWrongNetwork: (asset: string, network: string) =>
        `N’envoyez que du ${asset} sur le réseau ${network}. Les fonds envoyés depuis un autre réseau seront perdus.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Crédité après ${count.text} ${pluralFr(count, 'confirmation', 'confirmations')} du réseau, généralement en ${minutes.text} ${pluralFr(minutes, 'minute', 'minutes')} environ.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Dépôt minimum : ${amount} ${asset}.`,
      waiting: 'En attente du transfert',
      incoming: 'Dépôts en cours',
      incomingFirst: 'Repéré sur le réseau, en attente de la première confirmation',
      incomingProgress: (done: Count, total: Count) => `Confirmations : ${done.text} sur ${total.text}`,
      selectAsset: 'Choisissez une devise',
      noAssets: 'Le dépôt est temporairement indisponible. Réessayez plus tard.',
      noNetworks: 'Aucun réseau disponible pour cette devise : choisissez-en une autre.',
      addressFailed: 'Impossible d’obtenir l’adresse de dépôt',
      noticePermanent: 'L’adresse est permanente : utilisez-la pour tous vos prochains dépôts.',
      qrSaved: 'QR code enregistré',
      qrFailed: 'Impossible d’enregistrer le QR code',
      paymentLink: 'Lien pour le portefeuille',
      paymentLinkHint: 'Ouvrez-le dans votre portefeuille : le réseau et l’adresse se remplissent d’eux-mêmes.',
      copyLink: 'Copier le lien',
    },
    withdraw: {
      title: 'Retrait',
      history: 'Historique des retraits',
      asset: 'Devise du retrait',
      selectAsset: 'Choisissez une devise',
      network: 'Réseau du retrait',
      address: 'Adresse de retrait',
      addressPlaceholder: (network: string) => `Saisissez une adresse ${network}`,
      addressPaste: 'Coller l’adresse',
      addressFromBook: (label: string): string => `Confirmée dans votre carnet d’adresses · ${label}`,
      paste: 'Coller',
      addressInvalid: (network: string) => `Ne ressemble pas à une adresse ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `C’est une adresse ${detected}, mais ${selected} est sélectionné`,
      amount: 'Montant du retrait',
      amountPlaceholder: '0.00',
      networkFee: 'Frais de réseau',
      platformFee: (rate: string): string => `Frais de la plateforme (${rate} %)`,
      feeFree: 'sans frais pour l’instant',
      feeDynamic: 'au prix du gaz actuel',
      willReceive: 'Vous recevrez',
      notice: 'Avis important',
      noticeReview: 'Chaque demande est vérifiée avant l’envoi. Les fonds sont réservés immédiatement.',
      noticeTiming: 'Envoi sous 24 heures après approbation.',
      noticeMinimum: (amount: string, asset: string) =>
        `Retrait minimum : ${amount} ${asset}.`,
      noticeSupport:
        'Vérifiez l’adresse vous-même : un transfert vers une mauvaise adresse ne peut pas être annulé. L’assistance traite les litiges à partir du numéro de demande.',
      insufficient: 'Fonds insuffisants',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Reste aujourd’hui : ${remaining} sur ${limit} USDT.` : `Sans vérification : jusqu’à ${limit} USDT par jour, il reste ${remaining}. Faites vérifier votre identité pour relever la limite.`,
      addressNotConfirmed: 'Retraits uniquement vers les adresses du carnet, confirmées par e-mail',
      limitReached: (verified: boolean): string => (verified ? 'Limite de retrait quotidienne atteinte' : 'Limite quotidienne sans vérification atteinte : faites vérifier votre identité dans le profil'),
      belowMinimum: (amount: string, asset: string) => `Minimum ${amount} ${asset}`,
      confirmTitle: 'Vérifiez la demande',
      confirmSubtitle: 'Un retrait envoyé ne peut plus être annulé. Vérifiez le réseau et l’adresse.',
      confirmAction: 'Confirmer le retrait',
      failed: 'Impossible de créer la demande',
      submitted: 'Demande créée',
      submittedHint: 'Après vérification, les fonds partent sur le réseau. Suivez le statut dans vos demandes de retrait.',
      requestId: 'Numéro de demande',
      statusPending: 'En vérification',
      toRequests: 'Voir les demandes de retrait',
      empty: 'Aucune devise n’est disponible au retrait pour le moment',
      loadFailed: 'Impossible de charger la liste des devises',
      routeUnavailable: 'Les retraits sur ce réseau sont indisponibles pour le moment — choisissez un autre réseau',
      invalidAmount: 'Le montant ne convient pas : vérifiez la précision et les frais de réseau',
      edit: 'Corriger la demande',
    },
    withdrawals: {
      title: 'Demandes de retrait',
      toAddress: (address: string): string => `vers ${address}`,
      note: 'Une demande refusée ou échouée rend la réserve à votre compte. Ouvrez une demande pour voir sa transaction ou le motif.',
      empty: 'Aucune demande de retrait pour l’instant. Chaque demande affichera ici son statut, de la vérification à la confirmation par le réseau.',
      loadError: 'Impossible de charger les demandes',
      details: 'Demande de retrait',
      created: 'Créée le',
      transaction: 'Transaction',
      openExplorer: 'Ouvrir dans l’explorateur',
      reason: 'Motif',
      status: {
        pending: 'En vérification',
        approved: 'Approuvée',
        rejected: 'Refusée',
        broadcasting: 'Envoi en cours',
        sent: 'Envoyée',
        confirmed: 'Confirmée',
        failed: 'Échouée',
      },
      statusHint: {
        pending: 'La demande est en cours de vérification. Le montant est réservé sur votre compte.',
        approved: 'Vérifiée, en attente d’envoi sur le réseau.',
        rejected: 'La demande a été refusée ; la réserve est de retour sur votre compte.',
        broadcasting: 'La transaction est en cours d’envoi sur le réseau.',
        sent: 'La transaction est sur le réseau, en attente de confirmation.',
        confirmed: 'Le réseau a confirmé la transaction. Les fonds sont arrivés.',
        failed: 'L’envoi a échoué ; la réserve est de retour sur votre compte.',
      },
    },
    transfer: {
      title: 'Transfert',
      from: 'De',
      fromAvailable: (amount: string): string => `De · ${amount} disponibles`,
      to: 'Vers',
      submit: 'Transférer',
      swap: 'Inverser les comptes',
      selectFrom: 'Compte à débiter',
      selectTo: 'Compte à créditer',
      selectAsset: 'Choisissez une devise',
      after: 'Après le transfert',
      insufficient: 'Fonds insuffisants sur le compte à débiter',
      done: 'Transfert effectué',
      hint: 'Les transferts entre vos comptes sont instantanés et gratuits.',
      empty: 'Rien à transférer pour l’instant : alimentez d’abord votre compte.',
      failed: 'Impossible d’effectuer le transfert. Réessayez',
    },
    history: {
      title: 'Historique des opérations',
      deposit: 'Dépôt',
      withdrawal: 'Retrait',
      trade: 'Opération',
      fee: 'Frais',
      transfer: 'Transfert',
      pnl: 'Résultat',
      empty: 'Aucune opération pour l’instant. Les dépôts, retraits et opérations apparaîtront ici.',
      emptyFiltered: 'Aucune opération pour ce filtre',
      loadError: 'Impossible de charger l’historique',
      today: 'Aujourd’hui',
      yesterday: 'Hier',
      balanceAfter: (amount: string) => `solde ${amount}`,
      onlyAccount: (account: string) => `Seulement ${account}`,
      resetAccount: 'Afficher tous les comptes',
      accountFilter: 'Compte',
      allAccounts: 'Tous les comptes',
      withdrawalsLink: 'Statut des demandes de retrait',
      badges: {
        credited: 'Crédité',
        sent: 'Envoyé',
        fee: 'Frais',
        reserved: 'Réservé',
        returned: 'Restitué',
        released: 'Libéré',
        filled: 'Exécuté',
        settled: 'Réglé',
        liquidated: 'Liquidé',
        done: 'Effectué',
        adjusted: 'Ajusté',
        paid: 'Versé',
      },
      filters: {
        all: 'Toutes',
        rewards: 'Bonus',
        deposits: 'Dépôts',
        withdrawals: 'Retraits',
        trades: 'Opérations',
        transfers: 'Transferts',
      },
      types: {
        deposit: 'Dépôt',
        withdrawal: 'Retrait',
        withdrawal_fee: 'Frais de retrait',
        withdrawal_platform_fee: 'Frais de plateforme sur retrait',
        withdrawal_lock: 'Réserve pour retrait',
        withdrawal_release: 'Libération de la réserve de retrait',
        order_lock: 'Réserve pour ordre limite',
        order_release: 'Libération de la réserve d’ordre',
        bonus: 'Bonus promotionnel',
        trade_buy: 'Achat',
        trade_sell: 'Vente',
        trading_fee: 'Frais de trading',
        realized_pnl: 'Résultat de l’opération',
        signal_programmed_profit: 'Gain programmé du signal',
        funding: 'Financement',
        margin_lock: 'Réserve de marge',
        margin_release: 'Libération de marge',
        liquidation: 'Liquidation',
        transfer_in: 'Transfert entrant',
        transfer_out: 'Transfert sortant',
        copy_allocation: 'Alloué à la copie',
        copy_release: 'Retour de la copie',
        copy_commission: 'Commission de copy trading',
        referral: 'Rémunération de parrainage',
        adjustment: 'Ajustement',
      },
    },
  },

  trade: {
    title: 'Trading',
    menu: {
      account: 'Compte',
      trading: 'Trading',
      more: 'Plus',
    },
    long: 'Long',
    short: 'Short',
    buy: 'Acheter',
    sell: 'Vendre',
    marketOrder: 'Au marché',
    limitOrder: 'Limite',
    price: 'Prix',
    marketPrice: 'Au marché',
    quantity: 'Quantité',
    leverage: 'Levier',
    takeProfitStopLoss: 'Take-profit / stop-loss',
    positions: 'Positions',
    orders: 'Ordres',
    history: 'Historique',
    copyTrading: 'Copie de trades',
    unrealizedPnl: 'P&L latent',
    unrealizedPnlShort: 'P&L',
    margin: 'Marge',
    entry: 'Entrée',
    size: 'Taille',
    liquidation: 'Liquidation',
    close: 'Clôturer',
    orderBook: 'Carnet d’ordres',
    noPositions: 'Placez un ordre ci-dessus et la position apparaîtra ici.',
    balance: 'Solde',
    perpetual: 'Perpétuel',
    perpShort: 'Perp',
    fundingIn: (time: string) => `dans ${time}`,
    position: 'Position',
    closeAtMarket: 'Clôturer au marché',
    keepPosition: 'Garder la position',
    placeBuyOrder: 'Passer l’ordre d’achat',
    placeSellOrder: 'Passer l’ordre de vente',
    placeLongOrder: 'Passer l’ordre long',
    placeShortOrder: 'Passer l’ordre short',
    isolatedMargin: 'Marge isolée',
    crossMargin: 'Marge croisée',
    markShort: 'Marquage',
    liqPrice: 'Prix liq.',
    noPositionsTitle: 'Aucune position ouverte',
    noOrdersTitle: 'Aucun ordre actif',
    noHistoryTitle: 'Aucune opération pour l’instant',
    pair: 'Paire',
    selectPair: 'Choisissez une paire',
    sideLabel: 'Sens',
    orderType: 'Type d’ordre',
    sizeShare: 'Part du disponible',
    quantityShort: 'Qté',
    takeProfit: 'Take-profit',
    stopLoss: 'Stop-loss',
    tabsLabel: 'Mes opérations',
    openChart: 'Graphique',
    openLong: (symbol: string) => `Long ${symbol}`,
    openShort: (symbol: string) => `Short ${symbol}`,
    buyAsset: (symbol: string) => `Acheter ${symbol}`,
    sellAsset: (symbol: string) => `Vendre ${symbol}`,
    enterQuantity: 'Indiquez une quantité',
    insufficient: 'Fonds insuffisants sur le compte',
    belowMinNotional: (amount: string, asset: string) =>
      `Volume minimum de l’ordre : ${amount} ${asset}`,
    bookEmpty: 'Aucun ordre dans le carnet pour l’instant',
    bookUnavailable: 'Carnet d’ordres indisponible',
    tradingUnavailable:
      'Le service de trading ne répond pas : impossible d’envoyer un ordre pour le moment. Les soldes, dépôts et retraits fonctionnent normalement.',
    marketsFailed: 'Impossible de charger la liste des paires',
    noMarkets: 'Aucune paire pour l’instant : elles apparaîtront à l’ouverture des marchés.',
    confirmTitle: 'Vérifiez l’ordre',
    confirmSubtitle: 'Un ordre exécuté ne peut pas être annulé.',
    marginRequired: 'Marge utilisée',
    cost: 'À payer',
    liquidationEstimate: 'Liquidation estimée',
    submitted: 'Ordre envoyé',
    submitFailed: 'Impossible d’envoyer l’ordre',
    futuresSoon: 'Les futures sont temporairement indisponibles. Le trading spot fonctionne.',
    marginMode: 'Marge',
    cross: 'Croisée',
    isolated: 'Isolée',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Croisée : tout le solde futures soutient la position'
        : 'Isolée : seule la marge de cette position est en jeu',
    reduceOnly: 'Réduction seule',
    futuresAccount: 'Compte futures',
    equity: 'Capital',
    marginUsed: 'En marge',
    marginRatio: 'Niveau de risque',
    funding: 'Financement',
    fundingRate: 'Taux sur 8 h',
    nextFunding: 'Prochain',
    fundingPaid: 'Financement',
    protection: 'Protection',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take-profit et stop-loss',
    protectionSheetSubtitle: 'Le moteur clôturera la position au prix du marché dès que le cours touchera le niveau.',
    clearLevel: 'Aucun niveau',
    addMargin: 'Ajouter de la marge',
    removeMargin: 'Retirer de la marge',
    marginDelta: 'Montant de marge',
    marginDeltaHint: 'Position isolée : plus de marge éloigne la liquidation',
    addMarginKyc: 'L’ajout de marge s’ouvre une fois vos documents validés. Vous pouvez déjà retirer de la marge, modifier le take-profit et le stop-loss ou clôturer la position.',
    saveProtection: 'Enregistrer',
    protectionSaved: 'Protection mise à jour',
    protectionFailed: 'Impossible de mettre à jour la position',
    protectionInvalid: 'Le niveau est du mauvais côté du prix d’entrée',
    leverageTooHigh: 'Le levier dépasse le maximum autorisé pour cette paire',
    leverageTier: (cap: number, notional: string) => `Pour un volume de ${notional} USDT, levier jusqu’à ${cap}×`,
    futuresDisabled: 'Les ordres futures ne sont temporairement pas acceptés',
    liquidated: 'Liquidée',
    restingToast: (symbol: string, price: string) => `Ordre limite ${symbol} à ${price} placé, en attente du prix`,
    cancelledToast: 'Ordre annulé, fonds restitués',
    costSell: 'À recevoir',
    costBasis: 'Coût',
    enterProtection: 'Le niveau de take profit ou de stop loss n’est pas un prix valide',
    leverageLocked: (leverage: number) => `Le levier est fixé à ${leverage}× par la position ouverte. Fermez-la pour le changer`,
    marginReleased: 'Marge restituée',
    realizedEstimate: 'Résultat estimé',
    addsPosition: (quantity: string, asset: string) => `Ajoute ${quantity} ${asset} à la position ouverte`,
    reducesPosition: (quantity: string, asset: string) => `Réduit la position ouverte de ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Ferme la position ouverte de ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Ferme la position de ${closeQuantity} ${asset} et ouvre ${openQuantity} ${asset} dans l’autre sens`,
    byAccount: 'sur tout le compte',
    cancelledToastPlain: 'Ordre annulé',
    fillBuy: 'Achat',
    fillSell: 'Vente',
    marginFloor: (floor: string) => `La marge ne peut pas descendre sous la marge initiale de ${floor} USDT`,
    marginRemovable: (amount: string) => `Jusqu’à ${amount} USDT peuvent être retirés`,
    enterPrice: 'Indiquez le prix de l’ordre limite',
    markPrice: 'Prix de marquage',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'Le prix est obsolète : le flux de cotations a du retard. Réessayez dans quelques secondes.',
    marketDisabled: 'Le trading sur cette paire est suspendu',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `Ordre exécuté : ${side.toLocaleLowerCase('fr-FR')} ${quantity} ${asset} à ${price}`,
    closedToast: (symbol: string, pnl: string) => `${symbol} clôturée, résultat ${pnl} USDT`,
    positionsFailed: 'Impossible de charger les positions',
    noOrders: 'Un ordre limite attendra ici jusqu’à son exécution.',
    ordersFailed: 'Impossible de charger les ordres',
    noHistory: 'La première opération exécutée apparaîtra dans cette liste.',
    historyFailed: 'Impossible de charger l’historique des opérations',
    cancelOrder: 'Annuler',
    cancelFailed: 'Impossible d’annuler l’ordre',
    closeFailed: 'Impossible de clôturer la position',
    closeConfirmTitle: 'Clôturer la position ?',
    closeConfirmSubtitle:
      'La position sera clôturée au prix du marché et le résultat actuel deviendra définitif. Cette action est irréversible.',
  },

  chart: {
    title: 'Graphique',
    interval: 'Intervalle',
    chartType: 'Type de graphique',
    candles: 'Bougies',
    line: 'Ligne',
    high24h: 'Max 24 h',
    low24h: 'Min 24 h',
    volume24h: 'Volume 24 h',
    empty: 'Le graphique apparaîtra une fois des données accumulées pour cette paire',
    failed: 'Impossible de charger le graphique',
    unknownPair: 'Cette paire n’existe pas : choisissez-la dans l’onglet « Marchés »',
    toMarkets: 'Vers les marchés',
  },

  security: {
    title: 'Sécurité',
    subtitle: 'Le second facteur protège la connexion et les retraits',
    statusOn: 'Activé',
    statusOff: 'Désactivé',
    explain:
      'Le code de l’application d’authentification est demandé à la connexion et à chaque demande de retrait. Sans lui, le compte n’est protégé que par le mot de passe.',
    setup: 'Configurer l’authentificateur',
    step1: 'Scannez le code dans Google Authenticator, 1Password ou Aegis',
    secretLabel: 'Ou saisissez le secret manuellement',
    step2: 'Saisissez le code de l’application',
    enable: 'Activer',
    enabled: 'Second facteur activé',
    disableTitle: 'Désactiver le second facteur',
    disableHint: 'Saisissez le code actuel pour confirmer que le téléphone est entre vos mains',
    disable: 'Désactiver',
    disabled: 'Second facteur désactivé',
    codeInvalid: 'Le code n’a pas été accepté. Vérifiez l’heure du téléphone et saisissez un nouveau code',
    failed: 'Impossible de modifier les paramètres de sécurité',
    withdrawalCode: 'Code de confirmation',
    withdrawalCodeHint: 'Le retrait est protégé par le second facteur : saisissez le code de l’application',
    recoveryTitle: 'Codes de récupération',
    recoveryIntro: 'Conservez ces codes en lieu sûr. Chacun permet une connexion ou un retrait si le téléphone est indisponible. Ils ne seront plus affichés.',
    recoveryCopy: 'Copier les codes',
    recoveryCopied: 'Codes copiés',
    recoverySaved: 'J’ai enregistré les codes',
    recoveryLeft: (count: number): string => `Codes de récupération restants : ${count}`,
    recoveryRegenerate: 'Nouveaux codes de récupération',
    recoveryRegenerateHint: 'Les anciens codes cesseront de fonctionner. Saisissez le code de l’application',
    recoveryRegenerated: 'De nouveaux codes de récupération ont été émis',
    codeOrRecovery: 'Les six chiffres de l’application ou un code de récupération',
  },

  addressBook: {
    title: 'Adresses de retrait',
    hint: 'Une nouvelle adresse devient utilisable après confirmation via le lien envoyé par e-mail. Une session étrangère ne peut ainsi pas détourner vos fonds vers sa propre adresse.',
    add: 'Ajouter une adresse',
    address: 'Adresse',
    label: 'Nom',
    labelPlaceholder: 'Par exemple, Ledger',
    send: 'Confirmer par e-mail',
    saved: 'Adresses enregistrées',
    empty: 'Aucune adresse pour l’instant',
    emptyBody: 'Ajoutez l’adresse vers laquelle vous retirez : une fois confirmée depuis l’e-mail, elle se choisit d’un geste dans le formulaire de retrait.',
    statusConfirmed: 'confirmée',
    statusPending: 'en attente de confirmation par e-mail',
    resend: 'Renvoyer',
    resendLink: 'Renvoyer l’e-mail de confirmation',
    remove: 'Supprimer l’adresse',
    removeTitle: 'Supprimer cette adresse ?',
    removeHint: 'Les retraits vers cette adresse seront impossibles tant que vous ne l’aurez pas ajoutée et confirmée à nouveau.',
    linkSent: 'E-mail envoyé : ouvrez le lien pour confirmer l’adresse',
    alreadyConfirmed: 'Cette adresse est déjà confirmée',
    invalid: 'L’adresse ne correspond pas au réseau choisi',
    confirmed: (short: string): string => `Adresse ${short} confirmée`,
    confirmFailed: 'Le lien est invalide ou a expiré',
  },

  sessions: {
    title: 'Sessions et appareils',
    subtitle: 'Où votre compte est connecté',
    current: 'Cet appareil',
    started: 'Connexion',
    lastSeen: 'Activité',
    end: 'Terminer',
    endOthers: 'Terminer toutes les autres sessions',
    ended: 'Session terminée',
    endedOthers: (count: number): string => `Sessions terminées : ${count}`,
    empty: 'Aucune autre session active',
    failed: 'Impossible de charger les sessions',
    hint: 'Une session terminée perd l’accès sous quinze minutes : c’est la durée de vie d’une clé d’accès déjà émise.',
  },

  profile: {
    title: 'Profil',
    accountId: 'ID du compte',
    idCopied: 'ID copié',
    loadFailed: 'Impossible de charger le profil',
    partnerProgram: 'Programme partenaire',
    partnerProgramHint: 'Équipe, niveau et rémunérations',
    securityOn: 'Second facteur activé',
    securityOff: 'Activez le second facteur',

    verification: 'Vérification',
    verificationNone: 'Non effectuée',
    verificationPending: 'En cours',
    verificationInReview: 'En cours',
    verificationApproved: 'Validée',
    verificationRejected: 'Refusée',
    verificationResubmit: 'Nouveaux documents requis',
    verificationCta: 'Commencer',
    verificationCtaAgain: 'Renvoyer',
    verificationPrompt: 'Envoyez votre passeport : 2 minutes suffisent',
    verificationPendingHint: 'Un opérateur examine généralement les documents sous 24 heures.',
    verificationRejectedHint: 'L’opérateur n’a pas accepté les documents. Vous pouvez renvoyer la demande.',
    verificationOpen: 'Ouvrir la demande',

    withdrawalAddressesHint: 'Carnet d’adresses avec confirmation par e-mail',
    withdrawalAddresses: 'Adresses de retrait',
    addressesSubtitle: 'Adresses vers lesquelles vous avez déjà retiré des fonds',
    addressesEmpty: 'Vous n’avez pas encore effectué de retrait. L’adresse de votre première demande apparaîtra ici.',
    addressesFailed: 'Impossible de charger les adresses',
    addressCopied: 'Adresse copiée',

    security: 'Sécurité',
    accountAccess: 'E-mail et mot de passe',
    sessions: 'Sessions et appareils',
    sessionsHint: 'Où vous êtes connecté',
    securitySubtitle: 'Accès au compte',
    securityEmailVerified: 'E-mail confirmé',
    securityEmailUnverified: 'E-mail non confirmé',
    securityPassword: 'Changer le mot de passe',
    securityPasswordHint: 'Nous vous enverrons un lien de changement de mot de passe par e-mail',

    notifications: 'Notifications',
    pushOn: 'Push activé',
    pushOff: 'Push désactivé',
    pushToggle: 'Notifications push',
    pushUnsupported: 'Ce navigateur ne prend pas en charge les notifications push',
    pushDenied: 'Les notifications sont bloquées dans les réglages du navigateur',
    pushFailed: 'Impossible d’activer les notifications push',
    pushUnavailable: 'Les notifications push ne sont pas encore configurées côté serveur',


    theme: 'Thème',
    themeHint: 'Le thème noir est celui de la marque et économise la batterie sur OLED. Le clair convient au grand soleil. « Comme le système » suit le réglage du téléphone.',
    themeNames: { system: 'Comme le système', dark: 'Sombre', light: 'Clair' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Suit le réglage de l’appareil',
    language: 'Langue',
    languageHint: 'Langue de l’interface. Les montants gardent un format unique ; les dates suivent la langue.',

    support: 'Assistance',
    supportTitle: 'Contacter le support',
    supportChoose: 'Choisissez votre moyen de contact',
    supportVia: 'Telegram ou e-mail',
    supportTelegram: 'Écrire sur Telegram',
    supportEmail: 'Écrire par e-mail',
    about: 'À propos de l’application',
    version: 'Version',

    signOut: 'Se déconnecter',
    signOutTitle: 'Se déconnecter du compte ?',
    signOutBody:
      'Vos soldes, positions ouvertes et l’historique des signaux resteront en place : vous les retrouverez à la prochaine connexion. Vos autres appareils restent connectés.',

    kyc: {
      title: 'Vérification',
      intro:
        'Seul l’opérateur chargé de la vérification voit ces données. Le numéro du document est chiffré et n’est jamais affiché en entier.',
      loadFailed: 'Impossible de charger la demande',

      document: 'Document',
      documentType: 'Type de document',
      typePassport: 'Passeport',
      typeIdCard: 'Carte d’identité',
      typeDriverLicense: 'Permis de conduire',
      number: 'Numéro du document',
      numberPlaceholder: '4510 123456',
      numberHint: 'Série et numéro, tels qu’ils figurent sur le document',
      country: 'Pays de délivrance',
      countryPlaceholder: 'Choisissez un pays',
      issueDate: 'Date de délivrance',
      expiryDate: 'Valide jusqu’au',

      personal: 'Titulaire du document',
      lastName: 'Nom',
      firstName: 'Prénom',
      dateOfBirth: 'Date de naissance',

      documents: 'Photo du document',
      docMain: 'Page principale',
      docMainHint: 'La page avec la photo et le numéro',
      docFront: 'Recto',
      docFrontHint: 'La face avec la photo et le numéro',
      choose: 'Choisir un fichier',
      replace: 'Remplacer',
      remove: 'Retirer',
      fileHint: (size: string) => `JPG, PNG ou PDF, jusqu’à ${size} Mo`,
      megabytes: 'Mo',

      submit: 'Envoyer pour vérification',
      submitFailed: 'Impossible d’envoyer la demande. Vérifiez les données et réessayez',
      uploadFailed:
        'Les données ont été envoyées, mais les documents n’ont pas pu être téléversés. Essayez de les joindre à nouveau.',
      uploadRetry: 'Téléverser les documents',
      submitted: 'Demande envoyée',
      submittedHint: 'Un opérateur vérifiera les documents et mettra à jour le statut.',

      review: 'Demande en cours de vérification',
      reviewHint: 'Les données ne peuvent pas être modifiées pendant la vérification.',
      approved: 'Vérification validée',
      approvedHint: 'L’opérateur a confirmé les documents.',
      rejected: 'Demande refusée',
      rejectedHint: 'Corrigez les données et renvoyez la demande.',
      resubmit: 'Nouveaux documents requis',
      resubmitHint: 'Téléversez un document valide et renvoyez la demande.',
      rejectionReason: 'Motif',
      submittedAt: (date: string) => `Envoyée le ${date}`,
      attached: 'Documents joints',
      attachedEmpty: 'Aucun fichier n’est joint à la demande',
      startNew: 'Renvoyer',
      step: (current: number, total: number): string => `Étape ${current} sur ${total}`,

      gate: {

        title: 'Vérifiez votre identité pour utiliser vos fonds',

        body: 'Vous pouvez déposer dès maintenant. Le trading, les signaux, les transferts et les retraits s’ouvrent une fois vos documents validés, généralement sous 24 heures.',

        pending: 'Vos documents sont en cours de vérification : l’accès s’ouvre dès leur validation, généralement sous 24 heures.',

        action: 'Vérifier mon identité',

      },

      kinds: {
        passport_main: 'Page principale',
        passport_registration: 'Page de domiciliation',
        selfie_with_document: 'Selfie avec le document',
        proof_of_address: 'Justificatif de domicile',
      },

      errors: {
        required: 'Remplissez ce champ',
        number: 'Uniquement des lettres latines, des chiffres, des espaces et des tirets',
        name: 'Uniquement des lettres, des espaces, des apostrophes et des tirets',
        country: 'Choisissez un pays',
        date: 'Indiquez une date',
        issueFuture: 'La date de délivrance ne peut pas être dans le futur',
        expiryPast: 'Le document a expiré',
        expiryBeforeIssue: 'La date d’expiration doit être postérieure à la date de délivrance',
        age: 'La vérification est possible à partir de 18 ans',
        birthAfterIssue: 'La date de naissance doit précéder la date de délivrance',
        fileType: 'Un fichier JPG, PNG ou PDF convient',
        fileSize: (size: string) => `Le fichier dépasse ${size} Mo`,
        documentsRequired: 'Joignez une photo ou un scan du document',
        fileMissing: 'Ajoutez ce fichier',
      },
    },
  },

  countries: {
    search: 'Nom ou code du pays',
    popular: 'Fréquents',
    all: 'Tous les pays',
    nothing: 'Aucun résultat. Essayez le nom anglais ou un code, par exemple DE',
    found: (count: number): string => `Pays trouvés : ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Bonus promotionnel de ${amount} USDT débloqué et crédité sur le compte spot`,
    promoExpired: 'Le bonus promotionnel a expiré : les conditions n’ont pas été remplies à temps',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Votre filleul ${name} est qualifié : +${signals.text} ${pluralFr(signals, 'signal bonus', 'signaux bonus')}`
        : `Votre filleul ${name} est qualifié`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} ${pluralFr(signals, 'signal bonus', 'signaux bonus')}` : null,
        promo === null ? null : `un bonus promotionnel de ${promo} USDT bloqué jusqu’à ce que la condition soit remplie`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'Votre qualification par parrainage est validée' : `Qualification validée : ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Qualification annulée : un retrait a été demandé pendant la période de blocage. Les signaux bonus et le bonus promotionnel sont retirés'
          : 'Qualification annulée : les signaux bonus et le bonus promotionnel sont retirés'
        : `La qualification de votre filleul ${name} est annulée : les signaux bonus obtenus grâce à lui sont retirés`,
    referralRewardPaid: (amount: string): string => `${amount} USDT versés au titre du programme partenaire`,
    referralRewardRejected: (amount: string): string => `Une récompense de ${amount} USDT a été refusée par l’opérateur`,
    partnerLevelReached: (level: string, amount: string): string =>
      `Niveau ${level} atteint : une prime de ${amount} USDT est en attente de versement`,
    title: 'Notifications',
    settingsTitle: 'Paramètres des notifications',
    settingsHint: 'Une catégorie désactivée reste dans l’historique mais ne dérange pas : ni badge, ni push. Le push fonctionne lorsqu’il est activé dans le profil.',
    channelInApp: 'Dans l’application',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'Ce que le bot envoie dans votre chat. Une catégorie désactivée ici n’arrive pas dans Telegram mais reste dans l’application.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram connecté' : `Telegram connecté : ${name}`),
    telegramLinkedHint: 'Si ce n’était pas vous, touchez pour le déconnecter.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram déconnecté' : `Telegram déconnecté : ${name}`),
    telegramUnlinkedHint: 'Ce chat a été lié à un autre compte.',
    categories: { deposit: 'Dépôts', withdrawal: 'Retraits', copy: 'Copy trading', trade: 'Opérations et positions', signal: 'Signaux', promo: 'Bonus promotionnels', referral: 'Parrainage', kyc: 'Vérification', system: 'Système' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Tout marquer comme lu',
    markAllFailed: 'Impossible de marquer les notifications comme lues',
    loadFailed: 'Impossible de charger les notifications',
    empty:
      'Aucune notification pour l’instant. Les crédits, les réponses aux demandes de retrait, les signaux et les résultats des trades apparaîtront ici.',
    unread: 'Non lue',
    today: 'Aujourd’hui',
    yesterday: 'Hier',
    depositCredited: (amount: string, asset: string) =>
      `${amount} ${asset} crédités`,
    withdrawalApproved: 'Retrait approuvé',
    withdrawalRejected: 'Retrait refusé',
    withdrawalSent: 'Retrait envoyé sur le réseau',
    marginCall: 'Appel de marge',
    positionLiquidated: 'Position liquidée',
    copyOpened: (master: string) => `${master} a ouvert une position`,
    copyClosed: (master: string) => `${master} a clôturé une position`,
    copyStopLoss: 'Stop-loss de l’abonnement déclenché : positions clôturées, fonds restitués sur le compte spot',
    copyMasterStopped: 'Trader arrêté : la copie est terminée, les fonds sont restitués sur le compte spot',
    copyReleased: (amount: string) => `Reversé sur le compte spot : ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Commission versée au trader : ${commission} USDT sur ${profit} USDT de profit` : `Commission versée à ${master} : ${commission} USDT sur ${profit} USDT de profit`,
    signalLive: (title: string, until: string) => `Signal « ${title} » ouvert jusqu’à ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Position ${symbol} liquidée à ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol} : take-profit déclenché à ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol} : stop-loss déclenché à ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Ordre limite ${symbol} exécuté : ${quantity} à ${price}`,
    orderRejected: (symbol: string) => `Ordre limite ${symbol} non exécuté : le moteur a rejeté la transaction`,
    orderRejectedFunds: (symbol: string) => `Ordre limite ${symbol} non exécuté : marge insuffisante sur le compte futures`,
    orderRejectedLeverage: (symbol: string) => `Ordre limite ${symbol} non exécuté : le levier dépasse la limite pour cette taille`,
    kycApproved: 'Vérification validée',
    kycRejected: 'Vérification refusée',
    kycResubmit: 'La vérification nécessite de nouveaux documents',
    kycExpiring: (date: string) => `Votre document expire le ${date} : renouvelez la vérification`,
    kycExpired: 'Votre document a expiré : envoyez un document valide',
    signalClosed: (title: string) => `Signal clôturé : ${title}`,
    systemAlert: (check: string) => `Incident : ${check}`,
    systemRecovered: (check: string) => `Rétabli : ${check}`,
    heads: {
      signalLive: 'Signal ouvert',
      signalClosed: 'Signal clôturé',
      orderFilled: 'Ordre limite exécuté',
      orderRejected: 'Ordre limite non exécuté',
      takeProfit: 'Take-profit déclenché',
      stopLoss: 'Stop-loss déclenché',
      promoUnlocked: 'Bonus promotionnel débloqué',
      promoExpired: 'Bonus promotionnel expiré',
      referralQualified: 'Filleul qualifié',
      referralWelcome: 'Qualification validée',
      referralRevoked: 'Qualification annulée',
      rewardPaid: 'Récompense versée',
      rewardRejected: 'Récompense refusée',
      partnerLevelReached: 'Nouveau niveau partenaire',
      kycExpiring: 'Document bientôt expiré',
      kycExpired: 'Document expiré',
      systemAlert: 'Incident',
      systemRecovered: 'Rétabli',
    },
    fallback: {
      deposit: 'Dépôt crédité',
      withdrawal: 'Mise à jour de la demande de retrait',
      trade: 'Événement sur une position',
      copy: 'Événement de copy trading',
      referral: 'Événement du programme partenaire',
      system: 'Notification système',
    },
  },

  referrals: {
    title: 'Programme partenaire',
    rewardKind: {
      fee_rebate: 'Part des frais',
      signal_grant: 'Signaux supplémentaires',
      promotional_credit: 'Bonus promotionnel',
      deposit_bonus: 'Part du dépôt',
      welcome_bonus: 'Part de bienvenue',
      level_bonus: 'Prime de niveau',
    },
    rewardStatus: {
      pending_review: 'En vérification',
      approved: 'Approuvée',
      paid: 'Versée',
      rejected: 'Refusée',
      reversed: 'Annulée',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Bloqué',
      unlocked: 'Crédité',
      expired: 'Expiré',
      revoked: 'Révoqué',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Membre',
      bronze: 'Bronze',
      silver: 'Argent',
      gold: 'Or',
      platinum: 'Platine',
      ambassador: 'Ambassadeur',
    } as Record<string, string>,
    signalUnit: 'signaux',
    membersFrom: (count: Count): string => `à partir de ${count.text} ${pluralFr(count, 'membre', 'membres')}`,
    baseLevel: 'niveau de départ',
    depositFrom: (amount: string): string => `dépôt à partir de ${amount}`,
    noWeeklySignals: 'pas de signaux',
    perWeek: (count: Count): string => `${count.text} ${pluralFr(count, 'signal', 'signaux')}/sem.`,
    current: 'actuel',
    copyFailed: 'Impossible de copier',
    inviteText: 'Rejoignez 19CRT avec mon code',
    linkCopied: 'Lien copié',
    codeCopied: 'Code copié',
    loadError: 'Impossible de charger le programme partenaire',
    loadErrorHint: 'Vérifiez la connexion et réessayez',
    retry: 'Réessayer',
    disabled:
      'Le programme partenaire est temporairement suspendu. Vos statistiques et rémunérations sont conservées.',
    yourCode: 'Votre code',
    codeHint: 'Invitez directement : les conditions sont visibles avant l’inscription',
    openQr: 'Ouvrir le QR code d’invitation',
    qrCode: 'QR code',
    share: 'Partager',
    copy: 'Copier',
    yourLevel: 'Votre niveau',
    participant: 'Membre',
    allLevels: 'Tous les niveaux',
    progressLabel: 'Progression vers le niveau suivant',
    qualified: (count: Count): string => pluralFr(count, 'qualifié', 'qualifiés'),
    nextLevel: (remaining: Count, level: string): string => `encore ${remaining.text} jusqu’au niveau ${level}`,
    maxLevel: 'niveau maximum',
    invited: 'Invités',
    confirmed: 'Confirmés',
    paidUsdt: 'Versé, USDT',
    signalsReceived: 'Signaux reçus',
    transparentTitle: 'Rémunération transparente',
    transparentHint:
      'La rémunération est calculée uniquement sur les frais de trading réellement prélevés. Le dépôt de la personne invitée n’est jamais redistribué.',
    rewards: 'Rémunérations',
    rewardHistory: 'Historique des rémunérations',
    underReview: 'En vérification',
    emptyRewardsTitle: 'Aucune rémunération pour l’instant',
    emptyRewards: 'Les rémunérations apparaîtront après la première activité qualifiée de votre équipe.',
    levelsRules: 'Niveaux et règles',
    levelsRulesHint: 'Équipe, part des frais et signaux',
    inviteTitle: 'Invitation à 19CRT',
    inviteSubtitle: 'Le code sera associé à l’inscription',
    copyLink: 'Copier le lien',
    levelsTitle: 'Niveaux partenaires',
    levelsSubtitle: 'Seuls les membres directs qualifiés sont comptés',
    loadLevelsError: 'Impossible de charger les niveaux',
    levelTeam: 'Niveau et équipe',
    feeSignals: 'Frais · signaux',
    depositTiersTitle: 'Taux selon le dépôt',
    depositTiersHint: 'Plus le premier dépôt est élevé, plus la part est grande des deux côtés',
    tierDeposit: 'Dépôt',
    tierReferrer: 'Vous',
    tierWelcome: 'Filleul',
    tierFrom: (amount: string): string => `à partir de ${amount}`,
    levelBonus: 'Prime de niveau',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Règles du programme',
      qualification: (amount: string, asset: string): string =>
        `Qualification : un premier dépôt crédité d’au moins ${amount} ${asset} en une seule transaction.`,
      qualificationKyc: 'La vérification d’identité est requise.',
      welcome: (signals: Count, days: Count): string =>
        `Le nouveau membre reçoit ${signals.text} ${pluralFr(signals, 'signal bonus', 'signaux bonus')} pour ${days.text} ${pluralFr(days, 'jour', 'jours')}.`,
      referrer: (signals: Count, days: Count): string =>
        `Le parrain reçoit ${signals.text} ${pluralFr(signals, 'signal bonus', 'signaux bonus')} pour ${days.text} ${pluralFr(days, 'jour', 'jours')} par filleul qualifié.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Un bonus promotionnel de ${amount} USDT est bloqué et devient de l’argent sur le compte spot lorsque le volume d’opérations du nouveau membre atteint ${required} USDT en ${days.text} ${pluralFr(days, 'jour', 'jours')} ; sinon il expire.`,
      hold: (days: Count): string =>
        `Toute demande de retrait ${days.value.eq(1) ? 'dans le jour qui suit' : `dans les ${days.text} jours suivant`} la qualification l’annule, ainsi que les signaux bonus et le bonus promotionnel.`,
      feeShare:
        'La part de frais du niveau est calculée uniquement sur les frais de trading réellement perçus auprès du filleul. Les récompenses en argent sont vérifiées par l’opérateur et versées séparément : elles ne sont jamais créditées sur le solde.',
      secondLevel: (rate: string): string => `Le parrain du parrain reçoit ${rate} des mêmes frais.`,
      depositTiers:
        'La part du parrain et celle du filleul dépendent du montant du premier dépôt — voir le tableau de cette fenêtre.',
      balanceShare: (percent: string): string =>
        `Le dépôt doit représenter au moins ${percent} % du solde du parrain au moment du crédit.`,
      topup: (days: Count): string =>
        `Un dépôt supplémentaire ${days.value.eq(1) ? 'dans le jour qui suit' : `dans les ${days.text} jours suivant`} le premier s’y ajoute, le total est réévalué selon le tableau et la différence est versée une seule fois.`,
      premiumSignal: (count: string): string =>
        `L’accès permanent au signal premium s’ouvre à partir de ${count} filleuls qualifiés.`,
      antiabuse:
        'Un appareil ou une adresse partagés avec le parrain ou son équipe envoient la qualification en vérification chez l’opérateur.',
      paused: 'Le programme est suspendu par l’opérateur : aucune nouvelle qualification ni récompense n’est créée.',
    },
    disclaimer:
      'Les récompenses partenaires ne sont pas un revenu garanti. L’inscription, les dépôts et le volume de trading ne créent pas à eux seuls de versement en argent.',
    team: 'Équipe',
    teamStatus: {
      pending: 'en attente d’un dépôt',
      flagged: 'en vérification chez l’opérateur',
      qualified: 'qualifié',
      revoked: 'qualification annulée',
    },
    joined: (date: string): string => `depuis le ${date}`,
    showAll: (count: Count): string => `Tout afficher (${count.text})`,
    showLess: 'Réduire',
    promoTitle: 'Bonus promotionnel',
    promoLocked: (amount: string): string => `${amount} USDT bloqués`,
    promoUnlocked: (amount: string): string => `${amount} USDT crédités sur le compte spot`,
    promoExpired: 'Délai dépassé, le bonus est perdu',
    promoRevoked: 'Bonus révoqué',
    promoCondition: (traded: string, required: string): string => `Volume ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `jusqu’au ${date}`,
    promoHint: 'Le bonus devient de l’argent lorsque votre volume d’opérations atteint la condition avant l’échéance.',
  },

  signals: {
    title: 'Signaux',
    tabs: { today: 'Aujourd’hui', history: 'Historique' },
    tabsLabel: 'Sections des signaux',
    access: { public: 'Public', base: 'Base', premium: 'Premium', bonus: 'Bonus', agent: 'Partenaire' },
    status: { scheduled: 'Programmé', available: 'Disponible', executed: 'Exécuté', expired: 'Expiré', cancelled: 'Annulé' },
    side: { buy: 'Achat', sell: 'Vente' },
    direction: { buy: 'Long · achat', sell: 'Short · vente' },
    positionSide: { buy: 'Long', sell: 'Short' },
    localTime: 'heure locale',
    localClock: 'Heure locale',
    timeZone: (offset: string) => `Fuseau ${offset}`,
    reason: {
      unavailable: 'Ce signal n’est pas disponible pour le moment',
      already: 'Vous avez déjà exécuté ce signal',
      expired: 'La fenêtre d’exécution a expiré',
      notReleased: 'Ce signal n’est pas encore ouvert',
      cancelled: 'Ce signal a été annulé',
      balance: 'Fonds insuffisants sur le compte spot',
      balanceFutures: 'USDT insuffisants sur vos comptes spot et futures réunis',
      positionConflict: 'Vous avez une position ouverte sur ce marché dans le sens opposé : fermez-la pour exécuter le signal',
      price: 'Le prix de ce marché est indisponible pour le moment : réessayez dans quelques secondes',
      access: 'Ce signal requiert un autre niveau d’accès',
      kyc: 'Faites d’abord vérifier votre identité : une fois vos documents approuvés, vous pourrez exécuter le signal',
      minimum: 'Ce signal prend une part de votre solde total, inférieure à son minimum. Rechargez votre solde pour l’exécuter',
      maximum: 'Le montant dépasse le maximum de ce signal',
      belowMinimum: (amount: string) => `Ce signal prend une part de votre solde total, inférieure à son minimum de ${amount}\u00A0USDT. Rechargez votre solde pour l’exécuter`,
      aboveMaximum: (amount: string) => `Le montant dépasse le maximum de ce signal : au plus ${amount} USDT`,
      notFound: 'Aucun signal ne correspond à ce code',
      generic: 'L’action n’a pas pu être effectuée. Vérifiez les conditions et réessayez',
      network: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez',
    },
    enterCode: 'Saisir un code',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Saisissez le code du canal : vous verrez le sens, le montant et les conditions avant de confirmer', executed: 'Le résultat et les paramètres réels sont dans l’onglet Historique', scheduled: (time: string) => `Le code paraîtra dans le canal à ${time}` },
    executing: 'Exécution en cours',
    notExecuted: 'Non exécuté',
    executedLabel: 'Exécuté',
    loadSignalsError: 'Impossible de charger les signaux',
    noSignalsToday: 'Aucun signal aujourd’hui',
    loadErrorHint: 'Vérifiez votre connexion et réessayez',
    retry: 'Réessayer',
    signalAvailable: 'Signal disponible',
    nextSignal: 'Prochain signal',
    windowOpenUntil: (time: string) => `Fenêtre ouverte jusqu’à ${time}`,
    opensAt: (time: string) => `S’ouvre à ${time}`,
    execute: 'Exécuter',
    doneToday: 'C’est tout pour aujourd’hui',
    newBySchedule: 'De nouveaux signaux apparaîtront selon le calendrier',
    codeOverline: 'Code du signal',
    checkTerms: 'Vérifier les conditions',
    codeHint: 'Chaque code est publié sur notre chaîne Telegram — saisissez-le dès que vous le voyez.',
    openChannel: 'Ouvrir la chaîne',
    channelLink: 'chaîne Telegram',
    panelTitle: 'Code de l’opération',
    dealsTitle: 'Opérations par code',
    dealsEmpty: 'Pas encore d’opération par code',
    dealsEmptyHint: 'Saisissez un code de la chaîne\u00A0: l’opération et son résultat apparaîtront ici',
    codePlaceholder: 'Par exemple, 1QF9ET7RQ',
    codeRequired: 'Saisissez le code du canal',
    codeLabel: 'Code du signal',
    check: 'Vérifier',
    schedule: 'Calendrier',
    todaySignals: 'Signaux du jour',
    noSignals: 'Aucun signal pour le moment. Les nouveaux codes paraissent selon le calendrier sur notre chaîne Telegram.',
    riskDisclosure:
      'Un signal est une idée de trade, pas un résultat garanti. Avant de confirmer, vous verrez le sens, le montant, le levier, les frais et les conditions de clôture.',
    loadHistoryError: 'Impossible de charger l’historique des signaux',
    historyEmpty: 'L’historique est vide',
    historyEmptyHint: 'Les signaux exécutés et leurs paramètres réels apparaîtront ici.',
    toSignals: 'Vers les signaux',
    allExecutions: 'Toutes les exécutions',
    signalAccepted: 'Signal accepté',
    confirmation: 'Confirmation du signal',
    sentToTrading: 'L’ordre a été transmis au système de trading',
    checkBeforeExecute: 'Vérifiez les conditions avant l’exécution',
    acceptedHint: 'Appuyer à nouveau ne créera pas de second ordre.',
    ready: 'Terminé',
    account: 'Compte',
    spot: 'Spot',
    futures: 'Futures',
    perpetualMarket: 'futures',
    validUntil: 'Validité',
    signalName: 'Signal',
    until: (time: string) => `jusqu’à ${time}`,
    timeLeft: (countdown: string) => `encore ${countdown}`,
    priceGuide: 'Prix indicatif',
    entryGuide: 'Entrée indicative',
    marketPrice: 'Marché',
    stopLoss: 'Stop-loss',
    takeProfit: 'Take-profit',
    autoClose: 'Clôture automatique',
    inDuration: (duration: string) => `dans ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0j` : null, hours > 0 ? `${hours}\u00A0h` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0min` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Déjà exécuté',
    executionUnavailable: 'Exécution indisponible',
    executionAmount: 'Montant de l’exécution',
    marginAmount: 'Marge',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} de votre solde total · ${total}\u00A0USDT`,
    estimatedFee: 'Frais estimés',
    totalDebit: 'Total débité',
    positionSize: 'Taille de la position',
    willSell: 'À vendre',
    fundingNote: (amount: string) =>
      `${amount} USDT seront transférés de votre compte spot vers votre compte futures pour ce trade. À la clôture de la position, ce montant, ajusté du résultat, reviendra sur votre compte spot.`,
    executionRisk:
      'Un signal est une idée de trade, pas un résultat garanti. Le prix d’exécution peut différer de l’estimation et l’opération peut entraîner une perte. Ne confirmez qu’un risque que vous comprenez.',
    executionRiskFutures:
      'Un signal est une idée de trade, pas un résultat garanti. Le prix d’entrée peut différer de l’estimation, et le levier amplifie le gain comme la perte, jusqu’à la liquidation de la position. Ne confirmez qu’un risque que vous comprenez.',
    retryExecution: 'Relancer l’exécution',
    outcomeLabel: 'résultat, USDT',
    outcomeOpen: 'en cours',
    outcome: { take_profit: 'clôturé à l’objectif', stop_loss: 'clôturé au stop', time: 'clôturé à l’échéance', manual: 'clôturé manuellement', liquidation: 'clôturé par liquidation' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'À l’objectif', stop_loss: 'Au stop', time: 'À l’échéance', manual: 'Manuel', liquidation: 'Liquidé' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'En cours',
    executeFor: 'Exécuter jusqu’à',
    openLong: (amount: string) => `Ouvrir un long · ${amount} USDT`,
    openShort: (amount: string) => `Ouvrir un short · ${amount} USDT`,
    submittedToast: 'Signal transmis pour exécution',
    filledOf: (requested: string, filled: string) =>
      `Demandé ${requested} USDT, exécuté ${filled} USDT : la quantité a été arrondie au pas de lot.`,
    marginRounded: (requested: string) => `Demandé ${requested} USDT : la quantité a été arrondie au pas de lot.`,
    longOpened: (margin: string, leverage: number) => `Long ouvert : marge de ${margin} USDT, levier ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `Short ouvert : marge de ${margin} USDT, levier ${leverage}×.`,
    fundedFromSpot: (amount: string) => `${amount} USDT transférés depuis votre compte spot.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'au take-profit' : null, stopLoss ? 'au stop-loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Se clôturera d’elle-même dans ${duration}`
      const exits = duration === null ? levels : [...levels, `dans ${duration}`]
      const last = exits[exits.length - 1] ?? ''
      return `Se clôturera ${exits.length === 1 ? last : `${exits.slice(0, -1).join(', ')} ou ${last}`}`
    },
    openPosition: 'Voir la position',
    returnedToSpot: (amount: string) => `Reversé sur le compte spot : ${amount} USDT`,
    positionAccepted: 'La position est ouverte sur votre compte futures',
  },

  notFound: {
    title: 'Page introuvable',
    message: 'Cette page n’existe pas : le lien est périmé ou l’adresse contient une faute.',
    action: 'Vers l’accueil',
  },
}
